import { CONFIG_STORAGE_KEY } from '../../config';
import { PermissionModel } from '../../interfaces';
import * as Logger from '../../common/tools/logger';

let storageAvailable = false;

/**
 * Try to write a local storage item.
 * Local storage can be disabled, but remains available in the window.
 */
try {
  window.localStorage.setItem('__permissionTest', '__permissionTest');
  window.localStorage.removeItem('__permissionTest');

  storageAvailable = true;
// eslint-disable-next-line no-empty
} catch (error) {

}

/**
 * Get all PermissionModels from local storage
 */
export function getAllLocalPermissionModels(): PermissionModel[] {
  let localModels: PermissionModel[] = [];
  const localPerms = window.localStorage.getItem(CONFIG_STORAGE_KEY);

  if (!localPerms) {
    return localModels;
  }

  try {
    localModels = JSON.parse(localPerms);
  } catch (ex) {
    Logger.error('LocalStorage', 'getAllLocalPermissionModels', 'error parsing data from local storage', ex);
  }

  return localModels;
}

/**
 * @export
 * @function getLocalPermissionModel
 * @description Retrieve a permission model from localStorage
 * @param identity User identity
 * @return {PermissionModel}
 */
export function getLocalPermissionModel(identity?: string): PermissionModel {
  if (typeof identity !== 'string') {
    identity = null;
  }
  const emptyModel: PermissionModel = {
    tcString: null,
    tcfVersion: null,
    identity: identity || null,
    data: [],
  };

  /* istanbul ignore next if */
  if (!storageAvailable) {
    Logger.error('LocalStorage', 'getLocalPermissionModel', 'Storage not available.');
    return emptyModel;
  }

  const localPermissionList: PermissionModel[] = getAllLocalPermissionModels();
  const localPermissionModel = localPermissionList.find((model) => model.identity === identity);

  if (localPermissionModel) {
    Logger.debug('LocalStorage', 'getLocalPermissionModel', `using matching local model for identity ${identity}`);
  } else {
    Logger.info('LocalStorage', `getLocalPermissionModel`, `No locally stored permission model found for identity ${identity}.`);
    return emptyModel;
  }

  if (!(localPermissionModel.data instanceof Array)) {
    localPermissionModel.data = [];
  }

  return localPermissionModel;
}

/**
 * @export
 * @function storeLocalPermissionModel
 * @description Store the permission model in localStorage
 * @param {PermissionModel} permissionModel The permission model itself
 * @return {void}
 */
export function storeLocalPermissionModel(permissionModel: PermissionModel): void {
  /* istanbul ignore next if */
  if (!storageAvailable) {
    Logger.error('LocalStorage', `storeLocalPermissionModel`, `Storage not available.`);
    return;
  }

  const localPermissionList = getAllLocalPermissionModels();

  const modelIndex = localPermissionList.findIndex((localModel) => localModel.identity === permissionModel.identity);

  if (modelIndex >= 0) {
    Logger.debug('LocalStorage', 'storeLocalPermissionModel', `updating model for identity ${permissionModel.identity}`);
    localPermissionList[modelIndex] = permissionModel;
  } else {
    Logger.debug('LocalStorage', 'storeLocalPermissionModel', `storing new model for identity ${permissionModel.identity}`);
    localPermissionList.push(permissionModel);
  }

  window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(localPermissionList));
  Logger.debug('LocalStorage', `storeLocalPermissionModel`, `Successfully stored all permission data into local storage.`);
}
